<template>
    <b-modal
        :visible="true"
        title="자료로 평가 기준 초안 만들기"
        size="lg"
        no-close-on-backdrop
        no-close-on-esc
        :hide-header-close="submitting"
        hide-footer
        @hidden="$emit('close')"
    >
        <!-- 진행 중이던 작업 확인 -->
        <div v-if="phase === 'init'" class="text-center py-4">
            <b-spinner small class="mr-2" /> 확인 중...
        </div>

        <!-- 1) 자료 첨부 + 힌트 입력 -->
        <div v-else-if="phase === 'input'">
            <p class="text-muted small">
                평가 기준의 근거가 될 참조 자료를 첨부하면 AI가 평가 기준 초안을 작성합니다.
                초안은 검토·수정 후 <strong>저장</strong>해야 반영됩니다.
            </p>
            <b-form-group label="참조 자료 (1~5개)">
                <b-form-file
                    v-model="pickerFiles"
                    multiple
                    accept=".pdf,.txt,.md,.docx,.xlsx,.pptx"
                    placeholder="파일을 선택하거나 드래그하세요 (여러 번 추가 가능)"
                    browse-text="파일 선택"
                    @input="onFilesPicked"
                />
                <small class="text-muted">
                    pdf, txt, md, docx, xlsx, pptx / 파일당 20MB, 전체 50MB 이하.
                    스캔본(이미지) 문서는 내용이 반영되지 않습니다.
                </small>
            </b-form-group>
            <ul v-if="selectedFiles.length" class="list-unstyled small mb-2">
                <li
                    v-for="(f, idx) in selectedFiles"
                    :key="`${f.name}-${f.size}-${idx}`"
                    class="d-flex align-items-center mb-1"
                >
                    <span class="text-truncate mr-2" :title="f.name">{{ f.name }}</span>
                    <span class="text-muted mr-2 flex-shrink-0">{{ formatSize(f.size) }}</span>
                    <span v-if="fileIssue(f)" class="text-danger mr-2 flex-shrink-0">
                        {{ fileIssue(f) }}
                    </span>
                    <b-link class="text-danger flex-shrink-0" @click="removeFile(idx)">×</b-link>
                </li>
            </ul>
            <p v-if="batchIssue" class="text-danger small mb-2">{{ batchIssue }}</p>
            <b-form-group label="힌트 (선택)">
                <b-form-textarea
                    v-model="hint"
                    rows="3"
                    maxlength="2000"
                    placeholder="예: 협력사 안전보건 관리 기준 위주로 정리해 주세요"
                />
                <small class="text-muted">{{ hint.length }}/2000</small>
            </b-form-group>
            <b-alert :show="!!inputError" variant="danger" class="py-2">
                {{ inputError }}
            </b-alert>
            <div class="d-flex justify-content-end">
                <b-button
                    variant="secondary"
                    class="mr-2"
                    :disabled="submitting"
                    @click="$emit('close')"
                >
                    취소
                </b-button>
                <b-button variant="primary" :disabled="!canSubmit" @click="submit">
                    <b-spinner v-if="submitting" small class="mr-1" />초안 생성
                </b-button>
            </div>
        </div>

        <!-- 2) 초안 작성 중 (폴링) -->
        <div v-else-if="phase === 'running'">
            <b-alert v-if="resumeNotice" show variant="info" class="py-2 small">
                {{ resumeNotice }}
            </b-alert>
            <div class="text-center py-5">
                <b-spinner variant="primary" class="mb-3" />
                <p class="font-weight-bold mb-1">초안 작성 중...</p>
                <p class="text-muted small mb-0">
                    자료 분량에 따라 수십 초에서 몇 분까지 걸릴 수 있습니다.
                    <template v-if="startedLocally">({{ elapsedLabel }} 경과)</template>
                </p>
            </div>
        </div>

        <!-- 3) 완료 (검토 후 채워 넣기 / 이전 완성 초안 복원) -->
        <div v-else-if="phase === 'done'">
            <b-alert v-if="resumedDone" show variant="info" class="py-2 small">
                이전에 완성된 초안을 표시합니다. 새 자료로 다시 만들려면 [새로 만들기]를 누르세요.
            </b-alert>
            <b-alert v-else show variant="success" class="py-2">
                초안이 완성되었습니다. 검토 후 평가 기준 칸에 채워 넣으세요.
            </b-alert>
            <div class="mb-2">
                <div class="font-weight-bold small mb-1">참조 자료</div>
                <DraftSourcesList :sources="sources" />
            </div>
            <div class="font-weight-bold small mb-1">초안 미리보기</div>
            <div class="draft-preview border rounded p-2 small mb-3">{{ draft }}</div>
            <div class="d-flex justify-content-end">
                <b-button variant="outline-secondary" class="mr-auto" @click="resetToInput">
                    새로 만들기
                </b-button>
                <b-button variant="secondary" class="mr-2" @click="$emit('close')">
                    닫기
                </b-button>
                <b-button variant="primary" @click="$emit('apply', { draft, sources })">
                    평가 기준 칸에 채워 넣기
                </b-button>
            </div>
        </div>

        <!-- 4) 실패 -->
        <div v-else-if="phase === 'failed'">
            <b-alert show variant="danger">{{ failMessage }}</b-alert>
            <div v-if="sources.length" class="mb-3">
                <div class="font-weight-bold small mb-1">참조 자료 처리 결과</div>
                <DraftSourcesList :sources="sources" />
            </div>
            <div class="d-flex justify-content-end">
                <b-button variant="secondary" class="mr-2" @click="$emit('close')">
                    닫기
                </b-button>
                <b-button variant="primary" @click="resetToInput">다시 시도</b-button>
            </div>
        </div>
    </b-modal>
</template>

<script>
import DraftSourcesList from './DraftSourcesList.vue';

const POLL_INTERVAL_MS = 2500;
const MAX_FILES = 5;
const MAX_FILE_MB = 20;
const MAX_TOTAL_MB = 50;
const ALLOWED_EXTS = new Set(['pdf', 'txt', 'md', 'docx', 'xlsx', 'pptx']);
// status=failed 시 error_code별 사용자 문구 (계약: 이슈 #140 본문 + 코멘트)
const ERROR_CODE_MESSAGES = {
    NO_CONVERTIBLE_FILES: '자료에서 읽을 수 있는 내용이 없습니다. 텍스트가 포함된 문서인지 확인해 주세요.',
    RATE_LIMITED: '요청이 많아 초안을 만들지 못했습니다. 잠시 후 다시 시도해 주세요.',
    NO_GROUNDING: '참조 문서에서 판정 기준의 근거를 찾지 못했습니다. 문서를 확인하고 다시 시도하세요.',
    ALL_PROVIDERS_FAILED: '초안 생성에 실패했습니다. 잠시 후 다시 시도해 주세요.',
    EMPTY_DRAFT: '초안 생성에 실패했습니다. 잠시 후 다시 시도해 주세요.',
    INTERNAL_ERROR: '초안 생성에 실패했습니다. 잠시 후 다시 시도해 주세요.',
};

export default {
    name: 'EvalProfileDraftModal',
    components: { DraftSourcesList },
    props: {
        profileId: { type: Number, required: true },
    },
    data() {
        return {
            phase: 'init', // init | input | running | done | failed
            pickerFiles: [], // b-form-file 임시 바인딩 (선택 즉시 selectedFiles로 병합 후 비움)
            selectedFiles: [], // 첨부 목록의 단일 출처
            hint: '',
            inputError: '',
            submitting: false,
            jobId: null,
            draft: '',
            sources: [],
            failMessage: '',
            pollTimer: null,
            pollInFlight: false,
            pollErrorCount: 0,
            startedAt: null,
            startedLocally: false, // 이어보기(resume)면 시작 시각을 몰라 경과 표시를 숨긴다
            elapsedSec: 0,
            elapsedTimer: null,
            resumeNotice: '',
            resumedDone: false,
            isTornDown: false,
        };
    },
    computed: {
        totalSize() {
            return this.selectedFiles.reduce((sum, f) => sum + f.size, 0);
        },
        batchIssue() {
            if (this.selectedFiles.length > MAX_FILES) {
                return `참조 자료는 최대 ${MAX_FILES}개까지 첨부할 수 있습니다.`;
            }
            if (this.totalSize > MAX_TOTAL_MB * 1024 * 1024) {
                return `전체 파일 크기가 ${MAX_TOTAL_MB}MB를 초과합니다.`;
            }
            return '';
        },
        canSubmit() {
            if (this.submitting) return false;
            if (!this.selectedFiles.length) return false;
            if (this.batchIssue) return false;
            return !this.selectedFiles.some((f) => this.fileIssue(f));
        },
        elapsedLabel() {
            const m = Math.floor(this.elapsedSec / 60);
            const s = this.elapsedSec % 60;
            return m ? `${m}분 ${s}초` : `${s}초`;
        },
    },
    async created() {
        await this.checkActiveJob();
    },
    beforeDestroy() {
        // 파괴 후 도착하는 비동기 응답이 타이머를 되살리지 못하게 플래그를 먼저 세운다.
        this.isTornDown = true;
        this.stopPolling();
        this.stopElapsed();
    },
    methods: {
        // 파일 선택은 "추가"로 동작한다 — 기존 선택을 유지하고 새 선택을 병합한다.
        // (b-form-file은 재선택 시 교체 + 외부에서 배열 일부 제거해도 라벨이 안 바뀌므로,
        //  picker는 즉시 비우고 selectedFiles를 단일 출처로 쓴다.)
        onFilesPicked(picked) {
            const arr = Array.isArray(picked) ? picked : (picked ? [picked] : []);
            if (!arr.length) return;
            const seen = new Set(this.selectedFiles.map((f) => `${f.name}:${f.size}`));
            arr.forEach((f) => {
                const key = `${f.name}:${f.size}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    this.selectedFiles.push(f);
                }
            });
            this.$nextTick(() => { this.pickerFiles = []; });
        },
        fileIssue(f) {
            const name = f.name || '';
            const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
            if (!ALLOWED_EXTS.has(ext)) return '지원하지 않는 형식';
            if (f.size === 0) return '빈 파일';
            if (f.size > MAX_FILE_MB * 1024 * 1024) return `${MAX_FILE_MB}MB 초과`;
            return '';
        },
        removeFile(idx) {
            this.selectedFiles.splice(idx, 1);
        },
        formatSize(bytes) {
            if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
            return `${Math.max(1, Math.round(bytes / 1024))}KB`;
        },
        resetToInput() {
            // 첨부 파일과 힌트는 유지해 재시도를 쉽게 한다.
            this.phase = 'input';
            this.inputError = '';
            this.failMessage = '';
            this.jobId = null;
            this.resumeNotice = '';
            this.resumedDone = false;
            // 이전 실행의 결과가 새 실행의 실패 화면에 섞여 보이지 않도록 비운다.
            this.draft = '';
            this.sources = [];
        },
        // 진행 중이던 작업이 있으면 이어서 폴링하고, 없으면 최근 완성 초안을 복원한다
        // (창 재진입/새로고침으로 완성 초안이 유실되지 않도록 — 계약의 이력 API 활용).
        async checkActiveJob() {
            try {
                const { data } = await this.$axios.get(
                    `/eval-profiles/${this.profileId}/draft-context`,
                );
                if (this.isTornDown) return;
                const jobs = data.jobs || [];
                const active = jobs.find(
                    (j) => j.status === 'pending' || j.status === 'running',
                );
                if (active) {
                    this.jobId = active.job_id;
                    this.enterRunning({ notice: '진행 중이던 초안 작업을 이어서 표시합니다.' });
                    return;
                }
                const latest = jobs[0];
                if (latest && latest.status === 'done') {
                    const restored = await this.loadDoneJob(latest.job_id);
                    if (restored || this.isTornDown) return;
                }
            } catch (e) {
                if (this.isTornDown) return;
                // 이력 조회 실패는 치명적이지 않다 — 새 요청 입력으로 진행.
            }
            this.phase = 'input';
        },
        // 완성된 job의 초안을 미리보기로 복원한다. 자동 적용은 하지 않는다(수동 [채워 넣기]만).
        async loadDoneJob(jobId) {
            try {
                const { data } = await this.$axios.get(
                    `/eval-profiles/${this.profileId}/draft-context/${jobId}`,
                );
                if (this.isTornDown) return true;
                if (data.status === 'done' && data.draft) {
                    this.jobId = data.job_id;
                    this.draft = data.draft;
                    this.sources = data.sources || [];
                    this.resumedDone = true;
                    this.phase = 'done';
                    return true;
                }
            } catch (e) {
                // 복원 실패는 치명적이지 않다 — 입력 화면으로 진행.
            }
            return false;
        },
        enterRunning({ notice } = {}) {
            if (this.isTornDown) return;
            this.phase = 'running';
            this.resumeNotice = notice || '';
            this.startedLocally = !notice;
            this.pollErrorCount = 0;
            this.startedAt = Date.now();
            this.elapsedSec = 0;
            if (this.startedLocally) this.startElapsed();
            this.startPolling();
        },
        startPolling() {
            this.stopPolling();
            this.fetchStatus();
            this.pollTimer = setInterval(() => this.fetchStatus(), POLL_INTERVAL_MS);
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },
        startElapsed() {
            this.stopElapsed();
            this.elapsedTimer = setInterval(() => {
                this.elapsedSec = Math.round((Date.now() - this.startedAt) / 1000);
            }, 1000);
        },
        stopElapsed() {
            if (this.elapsedTimer) {
                clearInterval(this.elapsedTimer);
                this.elapsedTimer = null;
            }
        },
        finishPolling() {
            this.stopPolling();
            this.stopElapsed();
        },
        async submit() {
            if (!this.canSubmit) return;
            this.inputError = '';
            this.submitting = true;
            try {
                const fd = new FormData();
                // 필드명 'files' — 백엔드가 'files'/'files[]' 둘 다 수용 (이슈 #140 코멘트)
                this.selectedFiles.forEach((f) => fd.append('files', f));
                const hint = this.hint.trim();
                if (hint) fd.append('instruction_hint', hint);
                const { data } = await this.$axios.post(
                    `/eval-profiles/${this.profileId}/draft-context`,
                    fd,
                    { headers: { 'Content-Type': 'multipart/form-data' } },
                );
                if (this.isTornDown) return;
                this.jobId = data.job_id;
                this.enterRunning();
            } catch (e) {
                if (this.isTornDown) return;
                await this.handleSubmitError(e);
            } finally {
                this.submitting = false;
            }
        },
        async handleSubmitError(e) {
            const status = e.response && e.response.status;
            if (status === 409) {
                // 이미 진행 중인 작업이 있으면 그 작업을 이어서 지켜본다.
                // 단, 방금 첨부한 파일은 사용되지 않았음을 반드시 알린다.
                const resumed = await this.tryResumeActive();
                if (resumed || this.isTornDown) return;
                this.inputError = '이미 이 프로파일의 초안을 만드는 중입니다. 잠시 후 다시 시도해 주세요.';
                return;
            }
            this.inputError = this.httpErrorMessage(e);
        },
        async tryResumeActive() {
            try {
                const { data } = await this.$axios.get(
                    `/eval-profiles/${this.profileId}/draft-context`,
                );
                if (this.isTornDown) return true;
                const active = (data.jobs || []).find(
                    (j) => j.status === 'pending' || j.status === 'running',
                );
                if (active) {
                    this.jobId = active.job_id;
                    this.enterRunning({
                        notice: '이미 진행 중이던 초안 작업을 이어서 표시합니다. 방금 첨부한 자료는 이 작업에 사용되지 않았습니다.',
                    });
                    return true;
                }
            } catch (err) {
                // 이력 조회 실패 시 일반 409 문구로 안내한다.
            }
            return false;
        },
        // 내부 용어/영문 서버 메시지 노출 금지 — 상태코드별 고정 한국어 문구만 사용한다.
        httpErrorMessage(e) {
            if (!e.response) return '네트워크 오류가 발생했습니다. 연결 상태를 확인해 주세요.';
            switch (e.response.status) {
            case 400: return '요청이 올바르지 않습니다. 파일 형식·개수와 힌트 길이를 확인해 주세요.';
            case 404: return '프로파일을 찾을 수 없습니다. 화면을 새로고침해 주세요.';
            case 411: return '업로드 요청이 올바르지 않습니다. 다시 시도해 주세요.';
            case 413: return '파일이 너무 큽니다. 파일당 20MB, 전체 50MB 이하로 첨부해 주세요.';
            case 429: return '동시에 만들 수 있는 초안 개수를 초과했습니다. 진행 중인 작업이 끝난 뒤 다시 시도해 주세요.';
            case 502:
            case 503: return '일시적인 오류로 초안 생성을 시작하지 못했습니다. 잠시 후 다시 시도해 주세요.';
            default: return '초안 생성 요청에 실패했습니다. 잠시 후 다시 시도해 주세요.';
            }
        },
        async fetchStatus() {
            // 이전 요청이 안 끝났으면 이번 틱은 건너뛴다 (응답 겹침 → done 이중 emit 방지).
            if (this.pollInFlight) return;
            const jobId = this.jobId;
            if (!jobId) return;
            this.pollInFlight = true;
            try {
                const { data } = await this.$axios.get(
                    `/eval-profiles/${this.profileId}/draft-context/${jobId}`,
                );
                // 응답 도착 시점에 이미 종료/재시작된 폴링이면 무시한다.
                if (this.isTornDown || this.jobId !== jobId || this.phase !== 'running') return;
                this.pollErrorCount = 0;
                if (data.status === 'done') {
                    this.finishPolling();
                    this.draft = data.draft || '';
                    this.sources = data.sources || [];
                    this.phase = 'done';
                    this.$emit('done', { draft: this.draft, sources: this.sources });
                } else if (data.status === 'failed') {
                    this.finishPolling();
                    this.sources = data.sources || [];
                    this.failMessage = ERROR_CODE_MESSAGES[data.error_code]
                        || '초안 생성에 실패했습니다. 잠시 후 다시 시도해 주세요.';
                    this.phase = 'failed';
                }
                // pending/running 이면 다음 폴링을 기다린다.
            } catch (e) {
                if (this.isTornDown || this.jobId !== jobId || this.phase !== 'running') return;
                const status = e.response && e.response.status;
                if (status === 404) {
                    this.finishPolling();
                    this.failMessage = '작업 정보를 찾을 수 없습니다. 다시 시도해 주세요.';
                    this.phase = 'failed';
                    return;
                }
                // 일시적 네트워크 오류는 몇 차례 용인한다.
                this.pollErrorCount += 1;
                if (this.pollErrorCount >= 5) {
                    this.finishPolling();
                    this.failMessage = '진행 상태를 확인하지 못했습니다. 네트워크 확인 후 이 창을 다시 열면 이어서 확인합니다.';
                    this.phase = 'failed';
                }
            } finally {
                this.pollInFlight = false;
            }
        },
    },
};
</script>

<style scoped>
.draft-preview {
    max-height: 320px;
    overflow-y: auto;
    white-space: pre-wrap;
    background: #f8f9fa;
}
</style>
